// tar.gz upload adapter. The receipt-library serves packages as .zip or
// .tar.gz (GET /v1/receipts/<id>/package?format=tar.gz); this reads the
// .tar.gz form entirely in the browser — gunzip via the native
// DecompressionStream, then a minimal USTAR/PAX reader — and unpacks it into
// the same in-memory map the other adapters produce. No network, no dependency.

import { RECEIPT_SYNTHETIC_ROOT } from "./directory.js";
import { isUnsafeArchivePath, detectCommonRoot, normalizeArchivePath } from "./safe-path.js";
import type { LoaderResult } from "./types.js";

class ArchiveTooLargeError extends Error {}

// Cap on decompressed bytes, so a crafted tar.gz can't exhaust memory in the
// tab. Real receipt packages are a few KB to MB.
const MAX_UNCOMPRESSED_BYTES = 256 * 1024 * 1024; // 256 MiB

export async function loadReceiptFromTarGz(file: File): Promise<LoaderResult> {
  let tarBytes: Uint8Array;
  try {
    tarBytes = await gunzipWithCap(file);
  } catch (err) {
    if (err instanceof ArchiveTooLargeError) {
      return {
        error: { code: "targz_too_large", message: "tar.gz expands beyond the size limit." }
      };
    }
    return {
      error: {
        code: "targz_unreadable",
        message:
          err instanceof Error
            ? `tar.gz could not be decompressed: ${err.message}`
            : "tar.gz could not be decompressed."
      }
    };
  }

  let entries: Array<{ name: string; data: Uint8Array }>;
  try {
    entries = parseTar(tarBytes);
  } catch (err) {
    return {
      error: {
        code: "targz_unreadable",
        message: err instanceof Error ? err.message : "tar archive could not be read."
      }
    };
  }

  const root = detectCommonRoot(entries.map((e) => e.name));
  const files = new Map<string, string>();
  const decoder = new TextDecoder();
  for (const entry of entries) {
    // Reject tar-slip before the entry reaches the virtual filesystem, where a
    // `..` would otherwise normalize over a sibling (e.g. the real receipt).
    if (isUnsafeArchivePath(entry.name)) {
      return {
        error: {
          code: "targz_unsafe_entry",
          message: `tar.gz contains a path that escapes the receipt root: ${entry.name}`
        }
      };
    }
    // Canonicalize (`a/./b`, `a//b`, leading `./`) so equivalent spellings
    // collapse to one key — matching the virtual FS — before the duplicate
    // check; otherwise two spellings of the same file would both be accepted
    // and the chosen one would depend on archive order.
    const relativePath = normalizeArchivePath(stripRoot(entry.name, root));
    if (!relativePath) continue;
    // Two entries must never resolve to the same path — otherwise the chosen
    // receipt would depend on archive order.
    if (files.has(relativePath)) {
      return {
        error: {
          code: "targz_duplicate_entry",
          message: `tar.gz contains a duplicate path after unwrapping: ${relativePath}`
        }
      };
    }
    files.set(relativePath, decoder.decode(entry.data));
  }

  if (files.size === 0) {
    return { error: { code: "targz_empty", message: "tar.gz contained no files." } };
  }

  return {
    receipt: { packageDirectory: RECEIPT_SYNTHETIC_ROOT, files, sourceLabel: file.name }
  };
}

// Minimal tar reader: 512-byte header blocks; reads regular-file entries
// (typeflag '0' or NUL), honoring the USTAR name prefix and a preceding PAX
// extended header ('x') that carries a long `path=`. Directories, symlinks,
// and other entry types are skipped.
function parseTar(buf: Uint8Array): Array<{ name: string; data: Uint8Array }> {
  const out: Array<{ name: string; data: Uint8Array }> = [];
  const td = new TextDecoder();
  const field = (start: number, len: number) =>
    td
      .decode(buf.subarray(start, start + len))
      .replace(/\0.*$/, "")
      .trim();
  let off = 0;
  let paxPath: string | null = null;
  while (off + 512 <= buf.length) {
    // Two consecutive zero blocks terminate the archive.
    if (buf.subarray(off, off + 512).every((b) => b === 0)) break;
    const name = field(off, 100);
    const size = parseInt(field(off + 124, 12), 8) || 0;
    const typeflag = String.fromCharCode(buf[off + 156]);
    const prefix = field(off + 345, 155);
    off += 512;
    const dataStart = off;
    off += Math.ceil(size / 512) * 512;
    if (typeflag === "x") {
      // PAX extended header: records of the form "<len> key=value\n".
      const text = td.decode(buf.subarray(dataStart, dataStart + size));
      const match = text.match(/\d+ path=([^\n]*)\n/);
      paxPath = match ? match[1] : null;
      continue;
    }
    if (typeflag === "0" || typeflag === "\0" || typeflag === "") {
      const fullName = paxPath ?? (prefix ? `${prefix}/${name}` : name);
      paxPath = null;
      if (fullName) out.push({ name: fullName, data: buf.subarray(dataStart, dataStart + size) });
    } else {
      paxPath = null;
    }
  }
  return out;
}

function stripRoot(name: string, root: string): string {
  const normalized = name.replace(/^\/+/, "");
  if (root && normalized.startsWith(root)) return normalized.slice(root.length);
  return normalized;
}

// Gunzip the file, counting bytes as they stream out and aborting the moment
// the cap is crossed — so a decompression bomb (small compressed, huge
// decompressed) can't be fully materialized before the size is checked.
async function gunzipWithCap(file: File): Promise<Uint8Array> {
  const reader = file.stream().pipeThrough(new DecompressionStream("gzip")).getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > MAX_UNCOMPRESSED_BYTES) {
      await reader.cancel();
      throw new ArchiveTooLargeError();
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}
